import { toast } from 'sonner';
import { format } from 'date-fns';
import { EditorContent } from '@tiptap/react';
import { Controller, useForm } from 'react-hook-form';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CalendarIcon,
  Download,
  FileText,
  History,
  Loader2,
  MoreHorizontal,
  Paperclip,
  Plus,
  RefreshCw,
  Replace,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatFileSize } from '../utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { ExtractionStatus } from '../types';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { useEncounters } from '../api/encounters-api';
import { DocumentPreview } from './DocumentPreview';
import { EditorToolbar } from './editor/EditorToolbar';
import { DocumentTagPicker } from './DocumentTagPicker';
import { useDocumentEditor } from './editor/useDocumentEditor';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ACCEPTED_FILE_EXTENSIONS,
  extractionStatusColors,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  UPLOADED_DOCUMENT_TYPES,
  uploadedDocumentTypeLabels,
} from '../constants';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCreateRevision,
  useDeleteUploadedDocument,
  useDocumentDownloadUrl,
  useLinkEncounter,
  useTriggerExtraction,
  useUnlinkEncounter,
  useUpdateUploadedDocument,
  useUploadedDocument,
} from '../api/uploaded-documents-api';

// ─── Form types ──────────────────────────────────────────────

interface MetadataFormValues {
  document_date: Date;
  document_type: string;
  name: string;
  notes: string;
}

// ─── Component ───────────────────────────────────────────────

interface UploadedDocumentSheetProps {
  documentId: string | undefined;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string;
}

export function UploadedDocumentSheet({ open, patientId, documentId, onOpenChange }: UploadedDocumentSheetProps) {
  const { data: document } = useUploadedDocument(open ? documentId : undefined);
  const { data: encountersData } = useEncounters(open ? { limit: 100, patient_id: patientId } : undefined);
  const downloadUrl = useDocumentDownloadUrl(documentId);
  const updateMetadata = useUpdateUploadedDocument();
  const createRevision = useCreateRevision();
  const deleteMutation = useDeleteUploadedDocument();
  const linkEncounter = useLinkEncounter();
  const unlinkEncounter = useUnlinkEncounter();
  const triggerExtraction = useTriggerExtraction();

  const replaceFileRef = useRef<HTMLInputElement>(null);
  const [textDirty, setTextDirty] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  const revision = document?.current_revision;
  const extractionStatus = revision?.extraction_status as ExtractionStatus | undefined;

  // ─── React Hook Form for metadata ───────────────────────────
  // Memoize so Date objects keep a stable identity between renders. Without
  // this, RHF's `values` option would detect "new values" every render via
  // the fresh `new Date(...)` reference, reset the form, re-render, and loop.
  const serverValues = useMemo<MetadataFormValues>(
    () =>
      document
        ? {
            name: document.name,
            notes: document.notes ?? '',
            document_type: document.document_type,
            document_date: new Date(document.document_date + 'T00:00:00'),
          }
        : { name: '', notes: '', document_type: '', document_date: new Date(0) },
    [document],
  );

  const form = useForm<MetadataFormValues>({
    values: serverValues,
    resetOptions: { keepDirtyValues: false },
  });

  const {
    reset,
    control,
    register,
    formState: { isDirty: metadataDirty },
  } = form;

  // Reset text dirty when document changes
  useEffect(() => {
    setTextDirty(false);
  }, [document]);

  // ─── Tiptap editor for extracted text ─────────────────────────
  const handleEditorUpdate = useCallback(() => {
    setTextDirty(true);
  }, []);

  const editor = useDocumentEditor({
    onUpdate: handleEditorUpdate,
    editable: extractionStatus === 'extracted',
    initialContent: revision?.extracted_text ?? '',
  });

  const isDirty = metadataDirty || textDirty;

  // ─── Handlers ─────────────────────────────────────────────────

  const handleSave = form.handleSubmit(async (values) => {
    if (!document || !documentId) return;

    try {
      if (metadataDirty) {
        await updateMetadata.mutateAsync({
          documentId,
          data: {
            name: values.name.trim(),
            notes: values.notes.trim() || null,
            document_type: values.document_type,
            document_date: format(values.document_date, 'yyyy-MM-dd'),
          },
        });
      }

      if (textDirty && editor) {
        const content = editor.storage.markdown.getMarkdown();
        await createRevision.mutateAsync({
          documentId,
          extracted_text: content,
        });
        setTextDirty(false);
      }

      toast.success('Document saved');
    } catch {
      toast.error('Failed to save document');
    }
  });

  const handleDiscard = () => {
    if (!document) return;
    reset({
      name: document.name,
      notes: document.notes ?? '',
      document_type: document.document_type,
      document_date: new Date(document.document_date + 'T00:00:00'),
    });

    if (editor && revision?.extracted_text) {
      editor.commands.setContent(revision.extracted_text, false);
    }
    setTextDirty(false);
  };

  const handleDownload = async () => {
    const { data } = await downloadUrl.refetch();
    if (data?.url) {
      window.open(data.url, '_blank');
    } else {
      toast.error('Failed to get download URL');
    }
  };

  const handleReplaceFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0];
    if (!newFile || !documentId) return;

    const ext = '.' + newFile.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_FILE_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file type. Accepted: ${ACCEPTED_FILE_EXTENSIONS.join(', ')}`);
      return;
    }
    if (newFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    try {
      await createRevision.mutateAsync({ documentId, file: newFile });
      toast.success('File replaced. Text extraction has been re-triggered.');
    } catch {
      toast.error('Failed to replace file');
    }
  };

  const handleDelete = async () => {
    if (!documentId) return;
    try {
      await deleteMutation.mutateAsync(documentId);
      toast.success('Document deleted');
      onOpenChange(false);
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleRetryExtraction = async () => {
    if (!documentId) return;
    try {
      await triggerExtraction.mutateAsync(documentId);
      toast.success('Extraction re-triggered');
    } catch {
      toast.error('Failed to re-trigger extraction');
    }
  };

  const handleLinkEncounter = async (encounterId: string) => {
    if (!documentId) return;
    try {
      await linkEncounter.mutateAsync({ documentId, encounterId });
      setShowLinkPicker(false);
      toast.success('Encounter linked');
    } catch {
      toast.error('Failed to link encounter');
    }
  };

  const handleUnlinkEncounter = async (encounterId: string) => {
    if (!documentId) return;
    try {
      await unlinkEncounter.mutateAsync({ documentId, encounterId });
      toast.success('Encounter unlinked');
    } catch {
      toast.error('Failed to unlink encounter');
    }
  };

  // ─── Encounter link helpers ───────────────────────────────────
  const linkedEncounterIds = new Set(document?.linked_encounters?.map((e) => e.encounter_id) ?? []);
  const allEncounters = encountersData?.data ?? [];
  const unlinkableEncounters = allEncounters.filter((e) => !linkedEncounterIds.has(e.id));

  const isSaving = updateMetadata.isPending || createRevision.isPending;

  if (!document) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex flex-col w-full sm:max-w-4xl [&>button.absolute]:hidden">
          {/* Action bar */}
          <SheetHeader className="gap-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="h-5 w-5 text-amber-600 shrink-0" />
                <SheetTitle className="truncate">{document.name}</SheetTitle>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {/* Save button — appears when dirty */}
                {isDirty && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isSaving}
                      className="h-8 text-xs"
                      onClick={handleDiscard}>
                      Discard
                    </Button>
                    <Button size="sm" disabled={isSaving} className="h-8 text-xs" onClick={handleSave}>
                      {isSaving ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}

                {/* Desktop: text action buttons */}
                <div className="hidden sm:flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={handleDownload}>
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-red-600 hover:text-red-600"
                    onClick={() => setShowDeleteConfirm(true)}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete
                  </Button>
                </div>

                {/* Mobile: ⋯ dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8 sm:hidden">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleDownload}>
                      <Download className="mr-2 h-4 w-4" />
                      Download original
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => replaceFileRef.current?.click()}>
                      <Replace className="mr-2 h-4 w-4" />
                      Replace file
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toast.info('Revision history coming soon')}>
                      <History className="mr-2 h-4 w-4" />
                      Revision history
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Close button */}
                <SheetClose asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8">
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
            <SheetDescription className="flex flex-wrap items-center gap-2">
              <Badge className="text-xs" variant="secondary">
                {uploadedDocumentTypeLabels[document.document_type] ?? document.document_type}
              </Badge>
              {extractionStatus && extractionStatus !== 'extracted' && (
                <Badge variant="secondary" className={`text-xs ${extractionStatusColors[extractionStatus] ?? ''}`}>
                  {extractionStatus === 'extracting' ? 'Extracting...' : extractionStatus}
                </Badge>
              )}
              <span className="text-xs">
                Uploaded {format(new Date(document.created_at), "MMM d, yyyy 'at' h:mm a")}
              </span>
              {isDirty && textDirty && revision && (
                <span className="text-xs text-blue-600">· creates Rev {revision.revision_number + 1}</span>
              )}
            </SheetDescription>
          </SheetHeader>

          {/* Hidden file input for replace */}
          <input
            ref={replaceFileRef}
            type="file"
            className="hidden"
            accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
            onChange={handleReplaceFile}
          />

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {/* Metadata form */}
            <form className="space-y-3 px-4" onSubmit={(e) => e.preventDefault()}>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input id="edit-name" {...register('name')} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Controller
                    control={control}
                    name="document_type"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UPLOADED_DOCUMENT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Controller
                    control={control}
                    name="document_date"
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn('w-full justify-start text-left font-normal')}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(field.value, 'MMM d, yyyy')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-auto p-0">
                          <Calendar
                            initialFocus
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => date && field.onChange(date)}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea id="edit-notes" rows={2} placeholder="Additional notes..." {...register('notes')} />
              </div>

              {/* Tags — per-document assignment via the document_tags endpoint */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tags</Label>
                <DocumentTagPicker kind="uploaded" documentId={document.id} />
              </div>

              {/* Info row (read-only) */}
              {revision && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-2">
                  <span>{revision.content_type.split('/').pop()?.toUpperCase()} file</span>
                  <span>{formatFileSize(revision.file_size)}</span>
                  {revision.revision_number > 1 && <span>Rev {revision.revision_number}</span>}
                </div>
              )}
            </form>

            {/* Encounter links */}
            <div className="space-y-2 px-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Linked Encounters</Label>
                {allEncounters.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => setShowLinkPicker(!showLinkPicker)}>
                    <Plus className="mr-1 h-3 w-3" />
                    Link
                  </Button>
                )}
              </div>

              {document.linked_encounters && document.linked_encounters.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {document.linked_encounters.map((link) => (
                    <Badge key={link.encounter_id} variant="secondary" className="text-xs gap-1 pr-1">
                      {format(new Date(link.encounter_date), 'MMM d, yyyy')}
                      {link.encounter_type && ` \u00B7 ${link.encounter_type}`}
                      <button
                        type="button"
                        className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                        onClick={() => handleUnlinkEncounter(link.encounter_id)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : !showLinkPicker ? (
                <button
                  type="button"
                  className="flex items-center gap-2 w-full rounded-lg border border-dashed p-3 text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
                  onClick={() => setShowLinkPicker(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Link this document to an encounter
                </button>
              ) : null}

              {showLinkPicker && (
                <div className="rounded-lg border bg-popover p-1 space-y-0.5 max-h-48 overflow-y-auto">
                  {unlinkableEncounters.length > 0 ? (
                    unlinkableEncounters.map((enc) => (
                      <button
                        key={enc.id}
                        type="button"
                        className="flex items-center gap-2 w-full rounded-md px-2.5 py-2 text-xs hover:bg-accent text-left transition-colors"
                        onClick={() => handleLinkEncounter(enc.id)}>
                        <span className="font-medium">{format(new Date(enc.encounter_date), 'MMM d, yyyy')}</span>
                        {enc.encounter_type && (
                          <Badge variant="outline" className="text-[10px] h-4">
                            {enc.encounter_type}
                          </Badge>
                        )}
                        {enc.clinician?.name && <span className="text-muted-foreground">{enc.clinician.name}</span>}
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground p-2">All encounters already linked</p>
                  )}
                </div>
              )}
            </div>

            {/* Summary — short auto-generated paragraph (best-effort) */}
            <div className="px-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-2 block">Summary</Label>
              {extractionStatus && extractionStatus !== 'extracted' ? (
                <ExtractionStatusDisplay
                  status={extractionStatus}
                  isRetrying={triggerExtraction.isPending}
                  onRetry={handleRetryExtraction}
                />
              ) : revision?.summary ? (
                <p className="text-sm leading-relaxed">{revision.summary}</p>
              ) : (
                <p className="text-sm italic text-muted-foreground">No summary available.</p>
              )}
            </div>

            {/* Original document — PDF / image preview */}
            {revision && (
              <div className="px-4">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-2 block">Document</Label>
                <DocumentPreview
                  contentType={revision.content_type}
                  fileName={revision.file_name}
                  fileUrl={revision.file_url}
                />
              </div>
            )}

            {/* Extracted text — collapsed by default; for clinicians who need to
                correct OCR. We keep the editor mounted so the existing Save flow
                continues to detect text edits when the section is opened. */}
            <div className="px-4">
              <details className="rounded-md border bg-muted/30 px-3 py-2">
                <summary
                  aria-controls="uploaded-doc-extracted-text"
                  className="cursor-pointer text-xs text-muted-foreground">
                  Show extracted text
                </summary>
                <div id="uploaded-doc-extracted-text" role="region" aria-label="Extracted text">
                  {extractionStatus && extractionStatus !== 'extracted' ? (
                    <div className="mt-3">
                      <ExtractionStatusDisplay
                        status={extractionStatus}
                        isRetrying={triggerExtraction.isPending}
                        onRetry={handleRetryExtraction}
                      />
                    </div>
                  ) : (
                    <div className="mt-3 rounded-md border bg-background">
                      <div className="sticky top-0 z-10 bg-background border-b">
                        <EditorToolbar editor={editor} />
                      </div>
                      <EditorContent editor={editor} />
                    </div>
                  )}
                </div>
              </details>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &ldquo;{document.name}&rdquo; and all its revisions. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ExtractionStatusDisplay({
  status,
  onRetry,
  isRetrying,
}: {
  status: ExtractionStatus;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  switch (status) {
    case 'extracting':
      return (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Extracting text...
        </div>
      );
    case 'failed':
      return (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4" />
            Text extraction failed
          </div>
          <Button size="sm" variant="outline" disabled={isRetrying} onClick={onRetry}>
            {isRetrying ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
            Retry
          </Button>
        </div>
      );
    case 'not_applicable':
      return (
        <div className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          <FileText className="h-4 w-4" />
          Text extraction not available for this file type
        </div>
      );
    case 'pending':
      return (
        <div className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Waiting for extraction...
        </div>
      );
    default:
      return null;
  }
}
